import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaSpinner, FaEnvelope } from 'react-icons/fa';
import { FiUserPlus } from 'react-icons/fi';
import { PasswordStrengthIndicator, validatePassword } from '../utils/passwordValidation';

export default function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailValidation, setEmailValidation] = useState({ isValid: false, message: '', isChecking: false });
    const [studentType, setStudentType] = useState('physical'); // Default to physical
    const { register } = useAuth();
    const navigate = useNavigate();

    // Validate email format - Gmail only
    const validateEmailFormat = (email) => {
        const emailRegex = /^[^\s@]+@gmail\.com$/i;
        return emailRegex.test(email);
    };

    // Check if email already exists
    const checkEmailAvailability = async (emailValue) => {
        if (!emailValue || !validateEmailFormat(emailValue)) {
            setEmailValidation({ isValid: false, message: 'Only Gmail addresses allowed (@gmail.com)', isChecking: false });
            return;
        }

        setEmailValidation({ isValid: false, message: 'Checking...', isChecking: true });

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost/sudesh/api'}/auth/check_email.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailValue })
            });
            const data = await response.json();

            if (data.exists) {
                setEmailValidation({ isValid: false, message: 'Email already registered', isChecking: false });
            } else {
                setEmailValidation({ isValid: true, message: 'Email available', isChecking: false });
            }
        } catch (err) {
            // If API fails, just validate format
            setEmailValidation({ isValid: true, message: 'Valid format', isChecking: false });
        }
    };

    // Handle email change with debounce
    const handleEmailChange = (e) => {
        const emailValue = e.target.value;
        setEmail(emailValue);

        // Clear previous timeout
        if (window.emailTimeout) clearTimeout(window.emailTimeout);

        // Debounce email checking
        window.emailTimeout = setTimeout(() => {
            checkEmailAvailability(emailValue);
        }, 500);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!emailValidation.isValid) {
            return setError('Please enter a valid and available email address');
        }

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        const validation = validatePassword(password);
        if (!validation.isValid) {
            return setError('Password does not meet minimum requirements');
        }

        try {
            setError('');
            setLoading(true);
            await register(email, password, name, studentType);
            navigate('/dashboard');
        } catch (err) {
            setError('Failed to create an account: ' + err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-blue-50">
            {/* Background Decorations (Cloud/Sky effect) */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-200 via-blue-100 to-white"></div>
            <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

            {/* Register Card */}
            <div className="relative w-full max-w-md p-8 bg-white/60 backdrop-blur-2xl rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/50 m-4">

                {/* Logo/Icon */}
                <div className="flex justify-center mb-6">
                    <div className="w-14 h-14 bg-white rounded-2xl shadow-lg flex items-center justify-center text-gray-800">
                        {/* Using the icon as per consistent design request, user can swap for img if preferred like in Login */}
                        <FiUserPlus size={26} />
                    </div>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Create an account</h2>
                    <p className="mt-2 text-sm text-gray-500 font-medium">
                        Join Sudesh Maths and start learning today.
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50/80 border border-red-100 rounded-2xl text-red-600 text-sm font-medium text-center">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaUser className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Full Name"
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700 ml-1 mb-1">Student Type</label>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => setStudentType('physical')}
                                className={`px-4 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 border ${studentType === 'physical'
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-[1.02]'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                Physical Class
                            </button>
                            <button
                                type="button"
                                onClick={() => setStudentType('online')}
                                className={`px-4 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 border ${studentType === 'online'
                                    ? 'bg-purple-600 text-white border-purple-600 shadow-md transform scale-[1.02]'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                Online Class
                            </button>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaEnvelope className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={handleEmailChange}
                                className={`block w-full pl-11 pr-12 py-3.5 bg-gray-50/50 border rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 transition-all duration-200 ${email && emailValidation.isValid ? 'border-green-200 focus:ring-green-100/50' :
                                    email && !emailValidation.isValid && !emailValidation.isChecking ? 'border-red-200 focus:ring-red-100/50' :
                                        'border-transparent focus:ring-gray-100/50 focus:border-gray-200'
                                    }`}
                                placeholder="Gmail Address (your@gmail.com)"
                            />
                            {/* Validation Icon */}
                            {email && (
                                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                    {emailValidation.isChecking ? (
                                        <FaSpinner className="h-4 w-4 text-blue-400 animate-spin" />
                                    ) : emailValidation.isValid ? (
                                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Email Validation Message */}
                        {email && emailValidation.message && (
                            <div className={`mt-2 text-xs font-medium flex items-center gap-1.5 ml-1 ${emailValidation.isValid ? 'text-green-600' :
                                emailValidation.isChecking ? 'text-blue-500' : 'text-red-600'
                                }`}>
                                <span>{emailValidation.message}</span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Password"
                            />
                        </div>
                        <PasswordStrengthIndicator password={password} />
                    </div>

                    <div className="space-y-1">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-gray-600 transition-colors" />
                            </div>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border border-transparent rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-gray-100/50 focus:border-gray-200 transition-all duration-200"
                                placeholder="Confirm Password"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 flex items-center justify-center bg-gray-900 hover:bg-gray-800 text-white rounded-2xl font-bold transition-all duration-200 transform active:scale-95 shadow-lg shadow-gray-900/20 mt-4"
                    >
                        {loading ? <FaSpinner className="animate-spin h-5 w-5" /> : 'Create Account'}
                    </button>
                </form>




                {/* Sign In Link */}
                <p className="mt-8 text-center text-sm text-gray-500 font-medium">
                    Already have an account?{' '}
                    <Link to="/login" className="text-gray-900 font-bold hover:underline">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
